<template>
  <v-card class="calendar">
    <v-progress-linear v-if="loading" indeterminate color="primary"> </v-progress-linear>
    <div class="calendar_header">
      <div class="calendar_header_buttons">
        <v-btn
          :ripple="false"
          :width="30"
          :min-width="30"
          class="calendar_header_btn__switch outlined-btn outlined-btn__medium"
          outlined
          @click="movePrev"
          ><img class="calendar_header_icon__rotate" src="../../assets/icons/arrow.svg" />
        </v-btn>
        <DatePicker
          v-model="currentDate"
          className="date-picker__box-shadow calendar_header_date-picker"
          :isYearFirst="false"
          :inputValue="datePickerValue"
          :hideDetails="true"
          :dense="true"
          :height="34"
          @setDate="changeCurrentDate"
        />
        <v-btn
          :ripple="false"
          :width="30"
          :min-width="30"
          depressed
          outlined
          class="calendar_header_btn__switch outlined-btn outlined-btn__medium"
          @click="moveNext"
        >
          <img src="../../assets/icons/arrow.svg" />
        </v-btn>
      </div>
      <OutlinedButton @onClick="moveToday" :text="$t('practitioner.appointments.thisWeek')" />
    </div>
    <calendar
      ref="tuiCalendar"
      style="height: 800px"
      class="sm-calendar"
      :schedules="schedules"
      :view="view"
      :taskView="taskView"
      :scheduleView="scheduleView"
      :theme="theme"
      :week="week"
      :disableDblClick="true"
      :isReadOnly="isReadOnly"
      :template="templates"
      :useCreationPopup="false"
      :useDetailPopup="false"
      :usageStatistics="false"
      @beforeUpdateSchedule="onBeforeUpdateSchedule"
      @clickSchedule="onClickSchedule"
    >
    </calendar>
    <ScheduleDialog
      v-if="dialog"
      v-model="dialog"
      :schedule="scheduleInDialog"
      @onToggleDialog="toggleDialog"
      @onCancel="onCancelVisit"
      @onView="onView"
    />
    <CancelVisit
      v-if="cancelDialog"
      v-model="cancelDialog"
      :appointmentId="scheduleInDialog.raw.appointmentId"
      @toggleDialog="toggleCancelDialog"
      @success="onSuccessCancel"
    />
  </v-card>
</template>
<script>
import { Calendar } from "@toast-ui/vue-calendar";
import { DateTime } from "luxon";
import shortid from "shortid";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

import CancelVisit from "@/components/CancelVisit";
import DatePicker from "@/components/shared/DatePicker";
import OutlinedButton from "@/components/uikit/OutlinedButton";
import { snackBarEventBus, snackBarEventName } from "@/eventBuses/snackBar.eventBus";
import { calendarMixin } from "@/mixins/calendar.ts";
import { getWeek } from "@/utils/getWeek";
import { convertDayNames } from "@/utils/locales.helpers";
import { getColorByName, getRandomColor } from "@/utils/pallete.ts";

import ScheduleDialog from "./ScheduleDialog.vue";
const unavailableBlockStyles = {
  width: "100%",
  "background-image":
    "linear-gradient(135deg, #e7e8f2 4.17%, #f9f9fc 4.17%, #f9f9fc 50%, #e7e8f2 50%, #e7e8f2 54.17%, #f9f9fc 54.17%, #f9f9fc 100%)",
  "background-size": "12.00px 12.00px",
  "background-repeat": "repeat",
};
export default Vue.extend({
  name: "Calendar",
  mixins: [calendarMixin],
  components: {
    calendar: Calendar,
    DatePicker,
    OutlinedButton,
    ScheduleDialog,
    CancelVisit,
  },
  data: () => ({
    schedulesList: [],
    loading: false,
    dialog: false,
    cancelDialog: false,
    scheduleInDialog: {},
    isReadOnly: true,
    currentDate: new Date(),
  }),
  computed: {
    ...mapGetters("encounters", ["encounters"]),
    ...mapGetters("slots", ["availabilities", "slots", "weekends"]),
    ...mapGetters(["timeZone", "uid"]),
    schedules() {
      // TODO add here enum for cancelled
      return this.schedulesList
        .filter((s) => s.status !== "cancelled")
        .concat(this.inaccessibleHours)
        .map((s) => {
          const {
            color,
            patient: { id: patientId, name: patientName },
            chiefComplaint,
            complaintDescription,
            appointmentId,
          } = s;
          const isAvailable = s.status !== "noshow";
          const startDateTime = DateTime.fromMillis(Date.parse(s.start));
          const endDateTime = DateTime.fromMillis(Date.parse(s.end));
          const nowDateTime = DateTime.local();
          const isNow = startDateTime > endDateTime ? false : startDateTime < nowDateTime < endDateTime;
          const timeInterval = Math.abs(endDateTime.diff(startDateTime, "milliseconds").as("milliseconds")) / 60000;
          const height = this.determineScheduleHeight(timeInterval);
          const palleteCol = getColorByName(color);
          const isSchedulePast = endDateTime < DateTime.local();
          return {
            ...s,
            title: patientName,
            category: "time",
            isReadOnly: !patientName,
            customStyle: isAvailable
              ? this.getCustomStyles(height, palleteCol, isNow, isSchedulePast)
              : Object.keys(unavailableBlockStyles)
                  .map((k) => `${k}: ${unavailableBlockStyles[k]}`)
                  .join("; "),
            raw: {
              isNow,
              showTime: height > 20,
              available: isAvailable,
              patientId,
              timeInterval: `${startDateTime.toFormat("t")} - ${endDateTime.toFormat("t")}`,
              chiefComplaint,
              complaintDescription,
              appointmentId,
            },
          };
        });
    },
    templates() {
      return {
        time: (schedule) => {
          const {
            raw: { showTime, isNow, available, timeInterval },
          } = schedule;
          const isNowClassName = isNow ? " c-week-schedule--now" : "";
          if (!available) {
            return `<div class="c-week-schedule--unavailable"></div>`;
          }
          return `<div class="c-week-schedule-wrapper">
        ${showTime ? `<span class="c-week-schedule-time">${timeInterval}</span>` : ""}
        <span class="c-week-schedule${isNowClassName}">${schedule.title}</span>
        </div>`;
        },
        weekDayname: (model) => {
          const { isToday, day } = model;
          const weekends = this.weekends.map((w) => (w === "7" ? "0" : w));
          const isWeekend = weekends.some((weekend) => +weekend === day);
          if (isWeekend) {
            return `<div class="c-week-dayname">
            <div class="c-week-dayname--weekend">
            <span>${model.dayName}</span>
            <span>, ${model.date}</span>
            </div>
          </div>`;
          }
          return `<div class="c-week-dayname${isToday ? " c-week-dayname--today" : ""}">
          <span>${model.dayName}</span>
          <span>, ${model.date}</span>
        </div>`;
        },
      };
    },
    inaccessibleHours() {
      if (!this.slots) return [];
      const week = getWeek(this.currentDate);

      const value = week.reduce((acc, { year, month, day }) => {
        const dayIndex = DateTime.fromJSDate(new Date(+year, month, +day)).weekday;
        const slotsByDayIndex = this.slots[dayIndex];

        if (!slotsByDayIndex.length) return acc;

        const mapWithDate = this.slots[dayIndex].map((slot) => {
          const [startH, startM] = slot.start.split(":");
          const [endH, endM] = slot.end.split(":");
          return {
            start: new Date(year, month, day, startH, startM),
            end: new Date(year, month, day, endH, endM),
          };
        });
        const startDayTime = new Date(year, month, day, 0, 0);
        const endDayTime = new Date(year, month, day, 23, 59);

        const sortedDatesArray = mapWithDate.concat().sort((a, b) => (b.start < a.start ? 1 : -1));
        let nextStart;
        const unavailabilities = sortedDatesArray.reduce((a, v, i) => {
          const arrLength = sortedDatesArray.length;
          const obj = {
            id: shortid.generate(),
            start: nextStart,
            end: v.start,
            status: "noshow",
            patient: {
              id: "",
              name: "",
            },
          };

          if (arrLength === 1) {
            return a.concat([
              { ...obj, start: startDayTime, end: v.start },
              { ...obj, start: v.end, end: endDayTime },
            ]);
          }

          if (i === 0) {
            nextStart = v.end;
            obj.start = startDayTime;
          }

          if (i === arrLength - 1) {
            obj.end = endDayTime;
          }

          nextStart = v.end;
          return a.concat([obj]);
        }, []);
        return acc.concat(unavailabilities);
      }, []);
      return value;
    },
    week() {
      const dayNames = convertDayNames(this.$t("general.calendar.dayNames"), "Sunday");
      return {
        timezonesCollapsed: false,
        daynames: dayNames,
        startDayOfWeek: 1,
      };
    },
    datePickerValue() {
      const startWeek = DateTime.fromJSDate(this.currentDate).startOf("week").toFormat("LLLL, d");
      const endWeek = DateTime.fromJSDate(this.currentDate).endOf("week").toFormat("LLLL, d");
      return `${startWeek} - ${endWeek}`;
    },
  },
  methods: {
    ...mapActions("encounters", ["getAllEncounters"]),
    ...mapActions("slots", ["getPractitionerAvailability", "getPractitionerFreeSlots"]),
    ...mapActions("patients", ["getAllByPractitionerId"]),
    async onView() {
      await this.$router.push({ name: "PractitionerEncounter", params: { encounterId: this.scheduleInDialog.id } });
    },
    toggleDialog() {
      this.dialog = !this.dialog;
    },
    toggleCancelDialog() {
      this.dialog = !this.dialog;
    },
    onCancelVisit() {
      this.dialog = false;
      this.cancelDialog = true;
    },
    onSuccessCancel() {
      const { id } = this.scheduleInDialog;
      this.schedulesList = this.schedulesList.filter((sl) => sl.id !== id);
    },
    changeView(view) {
      this.view = view;
    },
    movePrev() {
      this.$refs.tuiCalendar.invoke("prev");
      this.refreshDate();
    },
    moveNext() {
      this.$refs.tuiCalendar.invoke("next");
      this.refreshDate();
    },
    moveToday() {
      this.$refs.tuiCalendar.invoke("today");
      this.refreshDate();
    },
    changeCurrentDate(newDate) {
      this.currentDate = newDate;
      this.$refs.tuiCalendar.invoke("setDate", newDate);
    },
    refreshDate() {
      const date = this.$refs.tuiCalendar.invoke("getDate");
      this.currentDate = date.toDate();
    },
    getCustomStyles(height, palleteCol, isNow, isSchedulePast) {
      const { color, gradient } = palleteCol;
      const customStyles = {
        height: `${height}px`,
        background: isNow ? "#33A3FF" : gradient,
        width: "94%",
        left: "8px",
        "border-bottom": isNow ? "none" : `2px solid ${color}`,
        "border-radius": "2px",
        color: isNow ? "rgba(255, 255, 255, 0.56)" : color,
        opacity: isSchedulePast ? "0.56" : "1",
      };
      return Object.keys(customStyles)
        .map((k) => `${k}: ${customStyles[k]}`)
        .join("; ");
    },
    onBeforeUpdateSchedule(e) {
      const {
        schedule: { id },
        changes: { end, start },
      } = e;
      this.scheduleList = this.scheduleList.map((s) =>
        s.id === id
          ? {
              ...s,
              ...(start ? { start } : {}),
              ...(end ? { end } : {}),
            }
          : s,
      );
    },
    onClickSchedule(e) {
      const { schedule } = e;
      if (!schedule.raw.available) return null;
      this.dialog = true;
      this.scheduleInDialog = schedule;
    },
    determineScheduleHeight(interval) {
      const heights = {
        30: 41,
        15: 20,
        60: 92,
      };
      return heights[interval];
    },
    adjustColors() {
      const colors = JSON.parse(localStorage.getItem("s-colors")) || [];
      const schedules = this.encounters.map((s) => {
        const { id } = s;
        const existedColor = colors.find((col) => col.id === id);
        return {
          ...s,
          color: existedColor ? existedColor.color : getRandomColor(),
        };
      });
      localStorage.removeItem("s-colors");
      localStorage.setItem("s-colors", JSON.stringify(schedules.map(({ id, color }) => ({ id, color }))));
      this.schedulesList = schedules;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      await this.getAllEncounters();
      await this.getPractitionerAvailability();
      await this.getAllByPractitionerId();
      await this.getPractitionerFreeSlots(this.uid);
    } catch (err) {
      snackBarEventBus.$emit(snackBarEventName, { message: err.message || err, type: "error" });
    }
    this.adjustColors();

    this.loading = false;
  },
});
</script>
<style>
.tui-full-calendar-time-date-schedule-block {
  z-index: 0 !important;
}
.sm-calendar {
  overflow-x: auto;
  width: 100%;
}
.tui-full-calendar-layout {
  min-width: 950px;
}
</style>
